import {
  normalizeBlogImageAlignment,
  normalizeBlogImageWidth,
  parseBlogImageMarkdown,
  parseBlogVideoMarkdown,
  resolveBlogVideo,
  serializeBlogImageMarkdown,
  serializeBlogVideoMarkdown,
} from "../blog/blogMedia";

export const BLOG_BLOCK_TYPES = Object.freeze({
  PARAGRAPH: "paragraph",
  HEADING: "heading",
  LIST: "list",
  QUOTE: "quote",
  IMAGE: "image",
  VIDEO: "video",
  DIVIDER: "divider",
});

let blockSequence = 0;

const generatedBlockId = (type = "block") => {
  blockSequence += 1;
  const cryptoApi = typeof window !== "undefined" ? window.crypto : undefined;
  const randomUUID = cryptoApi && cryptoApi.randomUUID;
  if (typeof randomUUID === "function") return `${type}-${randomUUID.call(cryptoApi)}`;
  return `${type}-${Date.now().toString(36)}-${blockSequence.toString(36)}`;
};

const hashText = (value) => {
  let hash = 5381;
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash << 5) + hash) ^ value.charCodeAt(index);
  }
  return (hash >>> 0).toString(36);
};

const parsedBlockId = (block, index) => {
  const signature = JSON.stringify({ ...block, id: undefined });
  return `${block.type}-${index + 1}-${hashText(signature)}`;
};

const cleanText = (value) => String(value == null ? "" : value).replace(/\r/g, "");

const defaultBlockFields = (type) => {
  switch (type) {
    case BLOG_BLOCK_TYPES.HEADING:
      return { type, level: 2, text: "" };
    case BLOG_BLOCK_TYPES.LIST:
      return { type, ordered: false, items: [""] };
    case BLOG_BLOCK_TYPES.QUOTE:
      return { type, text: "" };
    case BLOG_BLOCK_TYPES.IMAGE:
      return {
        type,
        src: "",
        alt: "",
        caption: "",
        width: "full",
        align: "center",
      };
    case BLOG_BLOCK_TYPES.VIDEO:
      return { type, url: "", caption: "" };
    case BLOG_BLOCK_TYPES.DIVIDER:
      return { type };
    case BLOG_BLOCK_TYPES.PARAGRAPH:
    default:
      return { type: BLOG_BLOCK_TYPES.PARAGRAPH, text: "" };
  }
};

/** Create a new editor block with a unique identity and sensible defaults. */
export const createBlock = (type = BLOG_BLOCK_TYPES.PARAGRAPH, values = {}) => {
  const normalizedType =
    type === "ordered-list" || type === "unordered-list"
      ? BLOG_BLOCK_TYPES.LIST
      : type === "video-embed"
        ? BLOG_BLOCK_TYPES.VIDEO
        : type;
  const defaults = defaultBlockFields(normalizedType);
  const listValues =
    type === "ordered-list"
      ? { ordered: true }
      : type === "unordered-list"
        ? { ordered: false }
        : {};

  return {
    ...defaults,
    ...listValues,
    ...values,
    type: defaults.type,
    id: values.id || generatedBlockId(defaults.type),
  };
};

const withParsedIds = (blocks) =>
  blocks.map((block, index) => ({ ...block, id: parsedBlockId(block, index) }));

/** Convert the Markdown subset rendered by ArticleContent into editable blocks. */
export const markdownToBlocks = (content = "") => {
  const lines = cleanText(content).split("\n");
  const blocks = [];
  let paragraphLines = [];

  const flushParagraph = () => {
    const text = paragraphLines.map((line) => line.trim()).filter(Boolean).join(" ");
    if (text) blocks.push({ type: BLOG_BLOCK_TYPES.PARAGRAPH, text });
    paragraphLines = [];
  };

  for (let index = 0; index < lines.length; index += 1) {
    const trimmed = lines[index].trim();
    if (!trimmed) {
      flushParagraph();
      continue;
    }

    const heading = trimmed.match(/^(#{1,3})\s+(.+)$/);
    const image = parseBlogImageMarkdown(trimmed);
    const video = parseBlogVideoMarkdown(trimmed);
    const unorderedItem = trimmed.match(/^[-*]\s+(.+)$/);
    const orderedItem = trimmed.match(/^\d+[.)]\s+(.+)$/);

    if (heading) {
      flushParagraph();
      blocks.push({
        type: BLOG_BLOCK_TYPES.HEADING,
        level: heading[1].length >= 3 ? 3 : 2,
        text: heading[2].trim(),
      });
      continue;
    }

    if (image) {
      flushParagraph();
      blocks.push({
        type: BLOG_BLOCK_TYPES.IMAGE,
        ...image,
      });
      continue;
    }

    if (video) {
      flushParagraph();
      blocks.push({
        type: BLOG_BLOCK_TYPES.VIDEO,
        ...video,
      });
      continue;
    }

    if (trimmed === "---") {
      flushParagraph();
      blocks.push({ type: BLOG_BLOCK_TYPES.DIVIDER });
      continue;
    }

    if (/^>\s?/.test(trimmed)) {
      flushParagraph();
      const quoteLines = [];
      while (index < lines.length && /^>\s?/.test(lines[index].trim())) {
        quoteLines.push(lines[index].trim().replace(/^>\s?/, ""));
        index += 1;
      }
      index -= 1;
      blocks.push({ type: BLOG_BLOCK_TYPES.QUOTE, text: quoteLines.join("\n").trim() });
      continue;
    }

    if (unorderedItem || orderedItem) {
      flushParagraph();
      const ordered = Boolean(orderedItem);
      const matcher = ordered ? /^\d+[.)]\s+(.+)$/ : /^[-*]\s+(.+)$/;
      const items = [];
      while (index < lines.length) {
        const item = lines[index].trim().match(matcher);
        if (!item) break;
        items.push(item[1].trim());
        index += 1;
      }
      index -= 1;
      blocks.push({ type: BLOG_BLOCK_TYPES.LIST, ordered, items });
      continue;
    }

    paragraphLines.push(trimmed);
  }

  flushParagraph();
  return withParsedIds(blocks);
};

const markdownForBlock = (block = {}) => {
  switch (block.type) {
    case BLOG_BLOCK_TYPES.HEADING: {
      const text = cleanText(block.text).trim();
      if (!text) return "";
      return `${Number(block.level) === 3 ? "###" : "##"} ${text}`;
    }
    case BLOG_BLOCK_TYPES.LIST:
    case "ordered-list":
    case "unordered-list": {
      const ordered = block.type === "ordered-list" || (block.type === BLOG_BLOCK_TYPES.LIST && block.ordered);
      const items = Array.isArray(block.items)
        ? block.items.map((item) => cleanText(item).trim()).filter(Boolean)
        : cleanText(block.text).split("\n").map((item) => item.trim()).filter(Boolean);
      return items.map((item, index) => `${ordered ? `${index + 1}.` : "-"} ${item}`).join("\n");
    }
    case BLOG_BLOCK_TYPES.QUOTE: {
      const lines = cleanText(block.text).split("\n").map((line) => line.trim()).filter(Boolean);
      return lines.map((line) => `> ${line}`).join("\n");
    }
    case BLOG_BLOCK_TYPES.IMAGE: {
      return serializeBlogImageMarkdown({
        ...block,
        width: normalizeBlogImageWidth(block.width),
        align: normalizeBlogImageAlignment(block.align),
      });
    }
    case BLOG_BLOCK_TYPES.VIDEO:
      return serializeBlogVideoMarkdown(block);
    case BLOG_BLOCK_TYPES.DIVIDER:
      return "---";
    case BLOG_BLOCK_TYPES.PARAGRAPH:
    default:
      return cleanText(block.text).trim();
  }
};

/** Convert editor blocks back to compact Markdown for the blog API. */
export const blocksToMarkdown = (blocks = []) =>
  (Array.isArray(blocks) ? blocks : [])
    .map(markdownForBlock)
    .filter(Boolean)
    .join("\n\n");

/** Return a provider-controlled HTTPS embed URL, or null for unsupported input. */
export const videoEmbedUrl = (url) => resolveBlogVideo(url)?.embedUrl || null;
